import { z } from 'zod'
import {
  COMMON_CLIENT_MESSAGES,
  type Color,
  type CommonServerMessage,
  type ErrorCode,
  type PlayerPublic,
} from '../protocol'
import { type CycleSnapshot, MAX_BOTS, MAX_CYCLES, type RoundCount } from './types'

export const cyclesSettingsSchema = z.object({
  rounds: z.union([z.literal(3), z.literal(5), z.literal(10)]),
  bots: z.number().int().min(0).max(MAX_BOTS),
})
export type CyclesSettings = z.infer<typeof cyclesSettingsSchema>
export const DEFAULT_CYCLES_SETTINGS: CyclesSettings = { rounds: 5, bots: 0 }
/** Room creation body: any subset of the settings, the rest default. */
export const createCyclesRoomBodySchema = cyclesSettingsSchema.partial()

export const headingSchema = z.union([z.literal(0), z.literal(1), z.literal(2), z.literal(3)])

/** Intent only: a heading to turn to, never a position. The server decides where the cycle is. */
export const cyclesClientMessageSchema = z.discriminatedUnion('type', [
  z.object({ type: z.literal('takeSeat') }),
  z.object({ type: z.literal('leave') }),
  z.object({ type: z.literal('start') }),
  z.object({ type: z.literal('next') }),
  /** Ask for a fresh full snapshot (after a missed tick frame or coming back from background). */
  z.object({ type: z.literal('sync') }),
  z.object({ type: z.literal('settings'), ...createCyclesRoomBodySchema.shape }),
  z.object({ type: z.literal('turn'), h: headingSchema }),
  ...COMMON_CLIENT_MESSAGES,
])
export type CyclesClientMessage = z.infer<typeof cyclesClientMessageSchema>

export type CyclesPhase = 'lobby' | 'countdown' | 'racing' | 'roundEnd' | 'results'

export interface CyclesRacer {
  profile: PlayerPublic
  /** Arena colour, unique per seat (a profile colour is only a preference). */
  color: Color
  online: boolean
  cpu: boolean
}

export interface CyclesRoundView {
  no: number
  /** Cycle order for this round; tick frames index into it. */
  cycleIds: string[]
  /** Server time the round starts moving (the countdown ends here). */
  startAt: number
  tick: number
  cycles: CycleSnapshot[]
}

export interface CyclesSnapshot {
  code: string
  seq: number
  phase: CyclesPhase
  hostId: string
  racers: CyclesRacer[]
  scores: Record<string, number>
  settings: CyclesSettings
  /** Countdown end, round cap, or round-end pause end; null in lobby/results. */
  deadlineAt: number | null
  countdownMs: number
  tickMs: number
  roundEndMs: number
  spectators: number
  round: CyclesRoundView | null
  lastRound: { placeById: Record<string, number>; pointsById: Record<string, number> } | null
}

/**
 * One simulation step, sent straight to the room's sockets: `c` holds `[x, y, heading]` per
 * cycle in `cycleIds` order (heading -1 = crashed); `at` is the tick's scheduled server time.
 */
export interface CyclesTickFrame {
  type: 'tick'
  r: number
  t: number
  at: number
  c: number[]
}

export const CYCLES_ERROR_CODES = [
  'NOT_HOST',
  'BAD_PHASE',
  'NOT_RACER',
  'NOT_RACING',
  'ROOM_FULL',
  'NEED_MORE_RACERS',
] as const
export type CyclesErrorCode = ErrorCode | (typeof CYCLES_ERROR_CODES)[number]

export const CYCLES_ERROR_TEXT: Record<(typeof CYCLES_ERROR_CODES)[number], string> = {
  NOT_HOST: 'Only the host can do that',
  BAD_PHASE: 'Not possible right now',
  NOT_RACER: 'You are watching this match',
  NOT_RACING: 'The round has not started',
  ROOM_FULL: `This arena is full (${MAX_CYCLES} cycles)`,
  NEED_MORE_RACERS: 'Add a bot or wait for another player',
}

export type CyclesServerMessage =
  | CommonServerMessage<CyclesErrorCode>
  | { type: 'state'; snapshot: CyclesSnapshot }
  | CyclesTickFrame

export interface CyclesRoomSummary {
  code: string
  phase: CyclesPhase
  racers: number
  hostNickname: string | null
}

export type { RoundCount }
